import { useState, useEffect } from 'react';
import { Builder } from '@builder.io/react';

interface Unit {
  title: string;
  description?: string;
  imageUrl?: string;
}

type ResponsiveSize = number | { desktop: number; tablet: number; mobile: number };

interface Props {
  units?: Unit[];
  fontFamily?: string;
  fontSize?: ResponsiveSize;
  titleFontFamily?: string;
  descriptionFontFamily?: string;
  titleFontSize?: ResponsiveSize;
  descriptionFontSize?: ResponsiveSize;
  titleFontWeight?: string;
  descriptionFontWeight?: string;
  titleFontStyle?: 'normal' | 'italic';
  descriptionFontStyle?: 'normal' | 'italic';
  titleColor?: string;
  descriptionColor?: string;
  imageWidth?: ResponsiveSize;
  maxMenuWidth?: ResponsiveSize;
  maxWidth?: ResponsiveSize;
}

function useDeviceType() {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  useEffect(() => {
    function updateDevice() {
      if (window.innerWidth <= 768) setDevice('mobile');
      else if (window.innerWidth <= 1024) setDevice('tablet');
      else setDevice('desktop');
    }
    updateDevice();
    window.addEventListener('resize', updateDevice);
    return () => window.removeEventListener('resize', updateDevice);
  }, []);
  return device;
}

function getResponsiveValue(value: ResponsiveSize | undefined, device: 'desktop' | 'tablet' | 'mobile') {
  if (typeof value === 'number' || value === undefined) return value;
  return value[device];
}

function cleanDescription(html = '') {
  if (!html) return '';
  // Remove empty <p></p> tags
  let cleaned = html.replace(/<p>\s*<\/p>/g, '');
  // Remove surrounding single <p> tags if entire content is wrapped
  cleaned = cleaned.replace(/^<p>([\s\S]*?)<\/p>$/i, '$1');
  // Trim whitespace
  cleaned = cleaned.trim();
  return cleaned;
}

export function HighlightedDropdown({
  units = [],
  fontFamily = 'GT-Walsheim-Pro-Regular',
  fontSize = 34,
  titleFontFamily,
  descriptionFontFamily,
  titleFontSize,
  descriptionFontSize,
  titleFontWeight = 'bold',
  descriptionFontWeight = 'normal',
  titleFontStyle = 'normal',
  descriptionFontStyle = 'normal',
  titleColor = '#e65300',
  descriptionColor = '#444',
  imageWidth = { desktop: 300, tablet: 250, mobile: 200 },
  maxMenuWidth = { desktop: 400, tablet: 350, mobile: 300 },
  maxWidth = { desktop: 1200, tablet: 900, mobile: 600 },
}: Props) {
  const device = useDeviceType();
  const [openIndex, setOpenIndex] = useState(units.length > 0 ? 0 : -1); // ✅ first item open by default

  const resolvedFontSize = getResponsiveValue(fontSize, device);
  const resolvedTitleFontSize = getResponsiveValue(titleFontSize ?? fontSize, device);
  const resolvedDescriptionFontSize = getResponsiveValue(descriptionFontSize ?? 24, device);
  const resolvedImageWidth = getResponsiveValue(imageWidth, device);
  const resolvedMaxMenuWidth = getResponsiveValue(maxMenuWidth, device);
  const resolvedMaxWidth = getResponsiveValue(maxWidth, device);

  return (
    <div
      style={{
        fontFamily,
        fontSize: resolvedFontSize,
        display: 'flex',
        flexDirection: device === 'mobile' ? 'column' : 'row',
        alignItems: device === 'mobile' ? 'stretch' : 'flex-start',
        padding: device === 'mobile' ? '1rem' : '2rem',
        margin: '0 auto',
        maxWidth: resolvedMaxWidth ? `${resolvedMaxWidth}px` : '100%',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Left Side: Dropdown Items */}
      <div
        style={{
          flex: '0 0 auto',
          width: resolvedMaxMenuWidth,
          marginRight: device !== 'mobile' ? '2rem' : 0,
        }}
      >
        {units.map((unit, index) => {
          const isOpen = openIndex === index;
          const cleanedDescription = cleanDescription(unit.description || '');

          return (
            <div key={index} style={{ marginBottom: device === 'mobile' ? '1.5rem' : '2rem' }}>
              {isOpen && cleanedDescription ? (
                <div
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    padding: '2rem',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                  onClick={() => {
                    // ✅ Don't allow fully collapsing all — reset to first item open
                    setOpenIndex(index === 0 ? 0 : -1);
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: titleColor,
                      fontWeight: titleFontWeight,
                      fontStyle: titleFontStyle,
                      fontSize: resolvedTitleFontSize,
                      fontFamily: titleFontFamily || fontFamily,
                    }}
                  >
                    <span style={{ marginRight: '1rem' }}>-</span>
                    {unit.title}
                  </div>

                  <div
                    style={{
                      color: descriptionColor,
                      fontSize: resolvedDescriptionFontSize,
                      fontWeight: descriptionFontWeight,
                      fontStyle: descriptionFontStyle,
                      fontFamily: descriptionFontFamily || fontFamily,
                      marginTop: '1.5rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {cleanedDescription.includes('<') ? (
                      <div dangerouslySetInnerHTML={{ __html: cleanedDescription }} />
                    ) : (
                      cleanedDescription
                    )}
                  </div>

                  {device === 'mobile' && unit.imageUrl && (
                    <img
                      src={unit.imageUrl}
                      alt={unit.title}
                      style={{
                        marginTop: '1.5rem',
                        width: '100%',
                        maxWidth: resolvedImageWidth,
                        objectFit: 'contain',
                        borderRadius: 6,
                      }}
                    />
                  )}
                </div>
              ) : (
                <div
                  onClick={() => setOpenIndex(index)}
                  style={{
                    cursor: 'pointer',
                    fontWeight: 'normal',
                    fontStyle: titleFontStyle,
                    color: titleColor,
                    fontSize: resolvedTitleFontSize,
                    fontFamily: titleFontFamily || fontFamily,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '1.5rem 2rem',
                  }}
                >
                  <span style={{ marginRight: '1rem' }}>+</span>
                  {unit.title}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop/Tablet Image */}
      {device !== 'mobile' && units[openIndex]?.imageUrl && (
        <div style={{ flexShrink: 0, alignSelf: 'stretch' }}>
          <img
            src={units[openIndex].imageUrl}
            alt={units[openIndex].title}
            style={{
              width: resolvedImageWidth,
              height: '100%',
              objectFit: 'cover',
              borderRadius: 6,
              display: 'block',
            }}
          />
        </div>
      )}
    </div>
  );
}

/* -------------------
   ✅ Builder.io Config
-------------------- */
Builder.registerComponent(HighlightedDropdown, {
  name: 'Highlighted Dropdown',
  inputs: [
    {
      name: 'units',
      type: 'list',
      subFields: [
        { name: 'title', type: 'text', defaultValue: 'Sample Unit' },
        { name: 'description', type: 'richText', defaultValue: '<p>This is a description</p>' },
        { name: 'imageUrl', type: 'file', allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'] },
      ],
    },
    { name: 'titleColor', type: 'color', defaultValue: '#e65300' },
    { name: 'descriptionColor', type: 'color', defaultValue: '#444' },
    {
      name: 'maxWidth',
      type: 'object',
      defaultValue: { desktop: 1200, tablet: 900, mobile: 600 },
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 1200 },
        { name: 'tablet', type: 'number', defaultValue: 900 },
        { name: 'mobile', type: 'number', defaultValue: 600 },
      ],
      friendlyName: 'Max Width (Desktop / Tablet / Mobile)',
    },
    {
      name: 'maxMenuWidth',
      type: 'object',
      defaultValue: { desktop: 400, tablet: 350, mobile: 300 },
      subFields: [
        { name: 'desktop', type: 'number', defaultValue: 400 },
        { name: 'tablet', type: 'number', defaultValue: 350 },
        { name: 'mobile', type: 'number', defaultValue: 300 },
      ],
      friendlyName: 'Menu Width (Desktop / Tablet / Mobile)',
    },
  ],
});
